/**
 * The literal fixture tables — the demo campaigns and sales agents — plus the
 * tier assignment that classifies them.
 */

import { DEMO_CONFIG, type PerformanceTierConfig } from './demoDataConfig';
import {
  DEFAULT_FUNNEL, getFunnelStageFromObjective, getFunnelStageFromGenericObjective,
  type FunnelConfig,
} from '@/config/funnelConfig';

// ============================================================================
// DEMO HELPER FUNCTIONS
// ============================================================================

/**
 * Get performance tier config
 */
export function getTierConfig(tier: string): PerformanceTierConfig {
  return DEMO_CONFIG.performanceProfiles[tier] || DEMO_CONFIG.performanceProfiles.tier1
}

/**
 * Generate campaign data
 */
export function generateCampaigns(): DemoCampaign[] {
  const baseCampaigns = DEMO_CAMPAIGNS.map((campaign) => {
    // Adjust campaign budgets based on demo budget
    const budgetMultiplier = DEMO_CONFIG.totalMonthlyBudget / 750000
    const adjustedBudget = Math.floor(campaign.budget * budgetMultiplier)

    return {
      ...campaign,
      budget: adjustedBudget,
      // Randomize performance tiers for variety
      performanceTier: assignPerformanceTier(),
    }
  })

  return baseCampaigns
}

/**
 * Assign a performance tier based on weighted distribution
 */
function assignPerformanceTier(): DemoCampaign['performanceTier'] {
  const rand = Math.random()
  if (rand < 0.3) return 'tier1' // 30% high performers
  if (rand < 0.7) return 'tier2' // 40% good performers
  if (rand < 0.9) return 'tier3' // 20% average
  return 'tier4' // 10% underperformers
}

// ============================================================================
// CAMPAIGN DATA
// ============================================================================

// ============================================================================
// DEMO CAMPAIGNS - Edit this section to customize demo content
// ============================================================================

/**
 * Demo campaign data with configurable funnel classification
 * Campaigns are automatically classified into funnel stages based on keywords
 * Edit the names below to see different classification results
 */
export interface DemoCampaign {
  id: string
  name: string
  platform: 'facebook' | 'google_display' | 'google_search' | 'performance_max' | 'tiktok' | 'line'
  status: 'active' | 'paused' | 'completed'
  /** Platform-specific objective (e.g., 'BRAND_AWARENESS', 'leads', 'traffic') */
  objective?: string
  budget: number
  startDate: Date
  endDate?: Date
  performanceTier: 'tier1' | 'tier2' | 'tier3' | 'tier4'
}

export const DEMO_CAMPAIGNS: DemoCampaign[] = [
  // ============================================================================
  // FACEBOOK CAMPAIGNS (8 campaigns)
  // ============================================================================
  {
    id: 'demo-camp-fb-1',
    name: 'Lead Generation - Enterprise Solutions',
    platform: 'facebook',
    status: 'active',
    objective: 'LEAD_GENERATION',
    budget: 75000,
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-fb-2',
    name: 'Brand Awareness - Q3 2024',
    platform: 'facebook',
    status: 'active',
    objective: 'BRAND_AWARENESS',
    budget: 60000,
    startDate: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-fb-3',
    name: 'Compare Service Plans - Guide',
    platform: 'facebook',
    status: 'active',
    objective: 'ENGAGEMENT',
    budget: 45000,
    startDate: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-fb-4',
    name: 'Lead Form - Service Inquiry',
    platform: 'facebook',
    status: 'paused',
    objective: 'CONVERSIONS',
    budget: 35000,
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier3',
  },
  {
    id: 'demo-camp-fb-5',
    name: 'Video Views - Features Explainer',
    platform: 'facebook',
    status: 'completed',
    objective: 'VIDEO_VIEWS',
    budget: 25000,
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    endDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-fb-6',
    name: 'Learn More - Service Benefits',
    platform: 'facebook',
    status: 'active',
    objective: 'REACH',
    budget: 40000,
    startDate: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-fb-7',
    name: 'Service Details - Features Information',
    platform: 'facebook',
    status: 'active',
    objective: 'BRAND_AWARENESS',
    budget: 35000,
    startDate: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-fb-8',
    name: 'Service Review - Customer Testimonials',
    platform: 'facebook',
    status: 'active',
    objective: 'BRAND_AWARENESS',
    budget: 30000,
    startDate: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },

  // ============================================================================
  // GOOGLE DISPLAY CAMPAIGNS (4 campaigns) - Awareness/Consideration focus
  // ============================================================================
  {
    id: 'demo-camp-gd-1',
    name: 'Display Remarketing - Interested Visitors',
    platform: 'google_display',
    status: 'active',
    objective: 'brand_awareness',
    budget: 35000,
    startDate: new Date(Date.now() - 65 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-gd-2',
    name: 'Gmail Ads - Service Benefits Guide',
    platform: 'google_display',
    status: 'active',
    objective: 'brand_awareness',
    budget: 25000,
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier3',
  },
  {
    id: 'demo-camp-gd-3',
    name: 'Service Brochure - Download Guide',
    platform: 'google_display',
    status: 'active',
    objective: 'brand_awareness',
    budget: 30000,
    startDate: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-gd-4',
    name: 'Learn More - Solution Features',
    platform: 'google_display',
    status: 'active',
    objective: 'brand_awareness',
    budget: 28000,
    startDate: new Date(Date.now() - 38 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },

  // ============================================================================
  // GOOGLE SEARCH CAMPAIGNS (4 campaigns) - Consideration/Conversion focus
  // ============================================================================
  {
    id: 'demo-camp-gs-1',
    name: 'Service Comparison - Features Guide',
    platform: 'google_search',
    status: 'active',
    objective: 'traffic',
    budget: 80000,
    startDate: new Date(Date.now() - 85 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-gs-2',
    name: 'Get Service Request - Apply Now',
    platform: 'google_search',
    status: 'active',
    objective: 'leads',
    budget: 40000,
    startDate: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-gs-3',
    name: 'Service Specifications - Features Details',
    platform: 'google_search',
    status: 'active',
    objective: 'traffic',
    budget: 45000,
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-gs-4',
    name: 'Review Service Plans - Compare Benefits',
    platform: 'google_search',
    status: 'active',
    objective: 'traffic',
    budget: 35000,
    startDate: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },

  // ============================================================================
  // PERFORMANCE MAX CAMPAIGNS (3 campaigns) - Conversion/Consideration optimization
  // ============================================================================
  {
    id: 'demo-camp-pm-1',
    name: 'Performance Max - Lead Generation',
    platform: 'performance_max',
    status: 'active',
    objective: 'performance_max',
    budget: 70000,
    startDate: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-pm-2',
    name: 'Performance Max - Features Guide',
    platform: 'performance_max',
    status: 'active',
    objective: 'performance_max',
    budget: 50000,
    startDate: new Date(Date.now() - 58 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-pm-3',
    name: 'Performance Max - Benefits Explainer',
    platform: 'performance_max',
    status: 'active',
    objective: 'performance_max',
    budget: 45000,
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },

  // ============================================================================
  // TIKTOK CAMPAIGNS (5 campaigns)
  // ============================================================================
  {
    id: 'demo-camp-tt-1',
    name: 'UGC Content - Brand Showcase',
    platform: 'tiktok',
    status: 'active',
    objective: 'reach',
    budget: 45000,
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-tt-2',
    name: 'Spark Ads - Service Benefits Explainer',
    platform: 'tiktok',
    status: 'active',
    objective: 'traffic',
    budget: 35000,
    startDate: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-tt-3',
    name: 'Lead Request - Contact Form',
    platform: 'tiktok',
    status: 'paused',
    objective: 'conversion',
    budget: 25000,
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier4',
  },
  {
    id: 'demo-camp-tt-4',
    name: 'Learn More - Solution Features Guide',
    platform: 'tiktok',
    status: 'active',
    objective: 'video_views',
    budget: 30000,
    startDate: new Date(Date.now() - 48 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-tt-5',
    name: 'Service Information - Educational Content',
    platform: 'tiktok',
    status: 'active',
    objective: 'reach',
    budget: 28000,
    startDate: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },

  // ============================================================================
  // LINE CAMPAIGNS (4 campaigns)
  // ============================================================================
  {
    id: 'demo-camp-line-1',
    name: 'OA Promotion - Service Request Weekend',
    platform: 'line',
    status: 'active',
    objective: 'conversion',
    budget: 40000,
    startDate: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier1',
  },
  {
    id: 'demo-camp-line-2',
    name: 'Friends Targeting - Plan Comparison Guide',
    platform: 'line',
    status: 'active',
    objective: 'website_conversion',
    budget: 30000,
    startDate: new Date(Date.now() - 50 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-line-3',
    name: 'Benefits Explainer - Features Details',
    platform: 'line',
    status: 'active',
    objective: 'traffic',
    budget: 25000,
    startDate: new Date(Date.now() - 44 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier2',
  },
  {
    id: 'demo-camp-line-4',
    name: 'Service Guide - Learn More',
    platform: 'line',
    status: 'active',
    objective: 'traffic',
    budget: 22000,
    startDate: new Date(Date.now() - 32 * 24 * 60 * 60 * 1000),
    performanceTier: 'tier3',
  },
]

// ============================================================================
// FUNNEL CLASSIFICATION - Uses configurable funnel
// ============================================================================

/**
 * Get demo campaigns with automatic funnel stage classification
 * Uses objective-based classification (not keyword matching)
 */
export function getDemoCampaignsFromClassified(
  _funnel: FunnelConfig = DEFAULT_FUNNEL
): DemoCampaign[] {
  return DEMO_CAMPAIGNS.map((campaign) => {
    // Use objective-based classification (same pattern as generateDemoCampaignsForChannel)
    let classifiedStage: string | undefined
    if (campaign.objective) {
      // Try platform-specific objective mapping first (normalizes platform internally)
      // Then fallback to generic objective mapping
      classifiedStage =
        getFunnelStageFromObjective(campaign.objective, campaign.platform || '') ||
        getFunnelStageFromGenericObjective(campaign.objective)
    }

    return {
      ...campaign,
      classifiedStage,
      // Keep original objective value, don't override it
      objective: campaign.objective,
    }
  })
}

// ============================================================================
// BROKER/AGENT DATA - Industry-agnostic terminology
// ============================================================================

/**
 * Demo broker/agent performance data
 * Industry-agnostic: uses "conversions" instead of "policies sold"
 */
interface DemoBrokerInput {
  team: string
  id: string
  name: string
  avatar: string
  status: string
}

export const DEMO_SALES_AGENTS: DemoBrokerInput[] = [
  { id: 'demo-agent-1', name: 'ปวีณา ศรีสวัสดิ์', avatar: 'ปว', status: 'active', team: 'ทีมขายภาคกลาง' },
  { id: 'demo-agent-2', name: 'ธนกร เจริญสุข', avatar: 'ธก', status: 'active', team: 'ทีมขายภาคกลาง' },
  { id: 'demo-agent-3', name: 'สุดารัตน์ พัฒนาการ', avatar: 'สด', status: 'active', team: 'ทีมขายภาคเหนือ' },
  { id: 'demo-agent-4', name: 'อนุชา ใจดี', avatar: 'อช', status: 'active', team: 'ทีมขายภาคตะวันออก' },
  { id: 'demo-agent-5', name: 'มัทนา รุ่งเรือง', avatar: 'มท', status: 'active', team: 'ทีม Online' },
  { id: 'demo-agent-6', name: 'พีรพล วงศ์สวรรค์', avatar: 'พพ', status: 'active', team: 'ทีม Online' },
  { id: 'demo-agent-7', name: 'จิราพร สมบูรณ์', avatar: 'จพ', status: 'active', team: 'ทีม Outbound' },
  { id: 'demo-agent-8', name: 'ณัฐพงษ์ ทองคำ', avatar: 'ณพ', status: 'active', team: 'ทีม Outbound' },
  { id: 'demo-agent-9', name: 'วรรณภา สังข์ทอง', avatar: 'วภ', status: 'active', team: 'ทีม Direct Sales' },
  { id: 'demo-agent-10', name: 'กิตติศักดิ์ ภักดี', avatar: 'กศ', status: 'active', team: 'ทีม Direct Sales' },
]
